import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List } from 'react-movable';
import KoobEmptyState from './KoobEmptyState';
import KoobPagination, { type KoobPaginationProps } from './KoobPagination';
import KoobLoader from './KoobLoader';
import KoobTableToolbar, { usePageToolbar } from './KoobTableToolbar';
import KoobTable, {
  KoobTableBody,
  KoobTableCell,
  KoobTableFooter,
  KoobTableHead,
  KoobTableHeader,
  KoobTableRow,
  getNextSort,
  type KoobTableSort,
} from './KoobTable';

export type KoobDataTableColumn<Row> = {
  key: string;
  label?: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  /** Column width; a truncating value (KoobCopy, KoobEntityChip) cuts
   *  short instead of widening it. */
  width?: string | number;
  /** Marks the column sortable without client-side sorting — for
   *  server-sorted data with the controlled `sort` prop. */
  sortable?: boolean;
  /** Sort identifier sent to onSortChange when it differs from `key`
   *  (e.g. a server-side order criteria). */
  sortKey?: string;
  /** Maps a row to a comparable value; its presence makes the column sortable. */
  sortValue?: (row: Row) => unknown;
  /** Cell renderer; defaults to `row[key]`. */
  render?: (row: Row) => React.ReactNode;
};

/** Bridges a URL search-params pair (from an app's useSearchParams hook)
 *  to KoobDataTable's controlled `sort`/`onSortChange` props, reading and
 *  writing the `order` + `direction` params. */
export const koobTableUrlSort = (
  searchParams: Record<string, unknown> | undefined,
  setSearchParams: (params: Record<string, unknown>) => void,
): { sort: KoobTableSort; onSortChange: (sort: KoobTableSort) => void } => ({
  sort: searchParams?.order
    ? {
        key: String(searchParams.order),
        direction: searchParams.direction === 'desc' ? 'desc' : 'asc',
      }
    : null,
  onSortChange: (next) => {
    const { order, direction, ...rest } = searchParams ?? {};
    setSearchParams(
      next ? { ...rest, order: next.key, direction: next.direction } : rest,
    );
  },
});

type KoobDataTableProps<Row> = {
  columns: KoobDataTableColumn<Row>[];
  data: Row[];
  /** One row at a fixed height with the loader, while the call is pending. */
  loading?: boolean;
  /** Shown when not loading and data is empty; defaults to a KoobEmptyState. */
  emptyState?: React.ReactNode;
  /** Controlled sort state — see koobTableUrlSort. Columns with a
   *  `sortValue` are still sorted client-side; `sortable` columns are
   *  assumed server-sorted. */
  sort?: KoobTableSort;
  defaultSort?: KoobTableSort;
  onSortChange?: (sort: KoobTableSort) => void;
  onRowClick?: (row: Row) => void;
  /** Optional per-row attributes (e.g. draggable, onDragStart) merged onto
   *  the <tr>, without changing any other consumer's behavior. */
  rowProps?: (row: Row) => React.HTMLAttributes<HTMLTableRowElement>;
  /**
   * Content for a row drawn as a single cell across every column (a group
   * heading, say); null/undefined keeps the row's regular cells.
   */
  fullWidthRow?: (row: Row) => React.ReactNode;
  /** Row actions (KoobTableAction nodes), rendered in a trailing column
   *  that only takes the space it needs — it does not participate in the
   *  %-based column sizing. Clicks inside never trigger onRowClick. */
  actions?: (row: Row) => React.ReactNode;
  /** Enables drag-to-reorder rows. Incompatible with sorting. */
  onMoveItem?: (args: { movedItem: Row; newIndex: number }) => void;
  /** Minimum table width; the wrapper scrolls horizontally below it. */
  minWidth?: string | number;
  footer?: React.ReactNode;
  /** A search field on top of the table, inside its card (KoobTableToolbar).
   *  Inside a KoobListPageLayout, the page's search, filters and actions go
   *  there by themselves. */
  search?: React.ReactNode;
  /** Filter controls beside the search. */
  filters?: React.ReactNode;
  /** Buttons at the right of the toolbar (export, create). */
  toolbarActions?: React.ReactNode;
  /** Free content inside the table card, above the column headers; drawn
   *  under the toolbar when both are given. */
  headerBar?: React.ReactNode;
  /** Renders a KoobPagination under the table; it hides itself when
   *  there are no results. */
  pagination?: KoobPaginationProps;
  /** For a table inside another card: no border of its own (see KoobTable). */
  embedded?: boolean;
};

const compare = (a: unknown, b: unknown) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

export default function KoobDataTable<Row>({
  columns,
  data,
  loading = false,
  emptyState,
  sort: controlledSort,
  defaultSort = null,
  onSortChange,
  onRowClick,
  rowProps,
  fullWidthRow,
  actions,
  onMoveItem,
  minWidth,
  footer,
  search,
  filters,
  toolbarActions,
  headerBar,
  pagination,
  embedded = false,
}: KoobDataTableProps<Row>) {
  const { t } = useTranslation('dataTable');
  const pageToolbar = usePageToolbar();
  const toolbar =
    search || filters || toolbarActions
      ? { search, filters, actions: toolbarActions }
      : (pageToolbar ?? {});
  const hasToolbar = Boolean(
    toolbar.search || toolbar.filters || toolbar.actions,
  );
  const isControlled = controlledSort !== undefined;
  const [internalSort, setInternalSort] = useState<KoobTableSort>(defaultSort);
  const sort = isControlled ? controlledSort : internalSort;

  const handleSort = (sortKey: string) => {
    const next = getNextSort(sort, sortKey);
    if (!isControlled) setInternalSort(next);
    onSortChange?.(next);
  };

  const sortedData = useMemo(() => {
    const sortValue = columns.find(
      (c) => (c.sortKey ?? c.key) === sort?.key,
    )?.sortValue;
    if (!sort || !sortValue) return data;
    const sorted = [...data].sort((a, b) =>
      compare(sortValue(a), sortValue(b)),
    );
    return sort.direction === 'desc' ? sorted.reverse() : sorted;
  }, [data, columns, sort]);

  const allColumns: KoobDataTableColumn<Row>[] = [
    ...(onMoveItem
      ? [
          {
            key: '__drag',
            width: '36px',
            render: () => (
              <span className="text-muted-foreground">
                <i
                  className="fa-regular fa-grip-dots-vertical"
                  style={{ fontSize: 20 }}
                />
              </span>
            ),
          } as KoobDataTableColumn<Row>,
        ]
      : []),
    ...columns,
    ...(actions
      ? [
          {
            key: '__actions',
            // 1% + nowrap content = the column shrinks to its content,
            // whatever % widths the other columns use
            width: '1%',
            render: (row: Row) => (
              <div
                className="flex items-center justify-end gap-1"
                onClick={(event) => event.stopPropagation()}
              >
                {actions(row)}
              </div>
            ),
          } as KoobDataTableColumn<Row>,
        ]
      : []),
  ];

  const renderRowCells = (row: Row) => {
    const fullWidth = fullWidthRow?.(row);
    if (fullWidth !== null && fullWidth !== undefined) {
      return (
        <KoobTableCell colSpan={allColumns.length}>{fullWidth}</KoobTableCell>
      );
    }
    return allColumns.map((column) => (
      <KoobTableCell
        key={column.key}
        align={column.align}
        // A width'd cell holding a truncating value (KoobCopy, KoobEntityChip)
        // keeps its width: max-width 0 stops that value from widening the
        // column, so it ends in "…"; 6rem keeps it readable when the other
        // columns take the room. Other content keeps its natural width.
        className={
          column.width
            ? '[&:has(.truncate)]:min-w-24 [&:has(.truncate)]:max-w-0'
            : undefined
        }
      >
        {column.render
          ? column.render(row)
          : (row as Record<string, React.ReactNode>)[column.key]}
      </KoobTableCell>
    ));
  };

  return (
    <KoobTable
      embedded={embedded}
      style={minWidth ? { minWidth } : undefined}
      headerBar={
        hasToolbar || headerBar ? (
          <>
            <KoobTableToolbar {...toolbar} />
            {headerBar}
          </>
        ) : undefined
      }
      footerBar={
        pagination && !loading ? (
          <KoobPagination embedded {...pagination} />
        ) : undefined
      }
    >
      <KoobTableHeader>
        <KoobTableRow>
          {allColumns.map((column) => {
            const sortKey = column.sortKey ?? column.key;
            const isSortable = Boolean(column.sortValue || column.sortable);
            return (
              <KoobTableHead
                key={column.key}
                align={column.align}
                style={column.width ? { width: column.width } : undefined}
                sortDirection={sort?.key === sortKey ? sort.direction : null}
                onSort={isSortable ? () => handleSort(sortKey) : undefined}
              >
                {column.label}
              </KoobTableHead>
            );
          })}
        </KoobTableRow>
      </KoobTableHeader>
      {!loading && onMoveItem ? (
        <List
          values={sortedData}
          onChange={({ oldIndex, newIndex }) =>
            onMoveItem({ movedItem: sortedData[oldIndex], newIndex })
          }
          renderList={({ children, props }) => (
            <KoobTableBody {...props}>{children}</KoobTableBody>
          )}
          renderItem={({ isDragged, value, props }) => (
            <KoobTableRow
              selected={isDragged}
              {...props}
              style={{ ...props.style }}
            >
              {renderRowCells(value)}
            </KoobTableRow>
          )}
        />
      ) : (
        <KoobTableBody>
          {loading && (
            // A table loads at a fixed height, its loader in the middle.
            <KoobTableRow className="hover:!bg-transparent">
              <KoobTableCell colSpan={allColumns.length} align="center">
                <div className="flex h-64 items-center justify-center">
                  <KoobLoader />
                </div>
              </KoobTableCell>
            </KoobTableRow>
          )}
          {!loading && sortedData.length === 0 && (
            <KoobTableRow>
              <KoobTableCell colSpan={allColumns.length}>
                {emptyState ?? (
                  <KoobEmptyState
                    variant="plain"
                    icon={<i className="fa-regular fa-table-list" />}
                    title={t('emptyStateMessage')}
                  />
                )}
              </KoobTableCell>
            </KoobTableRow>
          )}
          {!loading &&
            sortedData.map((row, index) => (
              <KoobTableRow
                key={index}
                {...(rowProps ? rowProps(row) : undefined)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {renderRowCells(row)}
              </KoobTableRow>
            ))}
        </KoobTableBody>
      )}
      {footer && (
        <KoobTableFooter>
          <KoobTableRow>
            <KoobTableCell colSpan={allColumns.length}>{footer}</KoobTableCell>
          </KoobTableRow>
        </KoobTableFooter>
      )}
    </KoobTable>
  );
}
